<template>
  <aside class="p-sidebar" :class="{ 'is-collapsed': collapsed }">
    <router-link :to="{ name: 'Sales' }" class="p-sidebar__brand">
      <div class="p-sidebar__logo">
        <img src="/favicon.png" alt="Pineapple" />
      </div>
      <div class="p-sidebar__brand-text">
        <div class="p-sidebar__brand-name">pineapple</div>
        <div class="p-sidebar__brand-sub">BACKOFFICE</div>
      </div>
    </router-link>

    <nav class="p-sidebar__nav">
      <template v-for="cat in categories" :key="cat.id">
        <!-- Direct link category (no submenu) -->
        <router-link
          v-if="cat.direct"
          :to="{ name: cat.direct }"
          class="p-cat p-cat--direct"
          :class="{ 'is-active': isCategoryActive(cat) }"
          @mouseenter="onCatEnter(cat)"
          @mouseleave="onCatLeave"
        >
          <span class="p-cat__accent" />
          <i class="material-icons-round p-cat__icon">{{ cat.icon }}</i>
          <span v-if="!collapsed" class="p-cat__label">{{ cat.label }}</span>
        </router-link>

        <!-- Expandable category (with submenu) -->
        <div
          v-else
          class="p-cat"
          :class="{ 'is-open': openCat === cat.id, 'is-active': isCategoryActive(cat) }"
          @mouseenter="onCatEnter(cat)"
          @mouseleave="onCatLeave"
        >
          <button
            type="button"
            class="p-cat__head"
            @click="onCatClick(cat)"
          >
            <span class="p-cat__accent" />
            <i class="material-icons-round p-cat__icon">{{ cat.icon }}</i>
            <template v-if="!collapsed">
              <span class="p-cat__label">{{ cat.label }}</span>
              <span v-if="cat.items.length" class="p-cat__count">{{ cat.items.length }}</span>
              <i class="material-icons-round p-cat__chev">
                {{ openCat === cat.id ? 'expand_less' : 'expand_more' }}
              </i>
            </template>
          </button>

          <!-- expanded items (only when not collapsed) -->
          <div
            v-if="!collapsed && openCat === cat.id && cat.items.length"
            class="p-cat__items"
          >
            <router-link
              v-for="it in cat.items"
              :key="it.name"
              :to="{ name: it.name }"
              class="p-cat__item"
              :class="{ 'is-active': isItemActive(it) }"
            >
              <span class="p-cat__item-dot" />
              {{ it.label }}
            </router-link>
          </div>

          <!-- flyout when collapsed -->
          <div
            v-if="collapsed && hoverCat === cat.id && cat.items.length"
            class="p-cat__flyout"
          >
            <div class="p-cat__flyout-title">{{ cat.label }}</div>
            <router-link
              v-for="it in cat.items"
              :key="it.name"
              :to="{ name: it.name }"
              class="p-cat__flyout-item"
              :class="{ 'is-active': isItemActive(it) }"
            >
              {{ it.label }}
            </router-link>
          </div>
        </div>
      </template>
    </nav>

    <div class="p-sidebar__footer">
      <div class="p-sidebar__user">
        <div class="p-sidebar__avatar">{{ userInitial }}</div>
        <div v-if="!collapsed" class="p-sidebar__user-info">
          <div class="p-sidebar__user-name">{{ userName }}</div>
          <div class="p-sidebar__user-role">Admin · ONLINE</div>
        </div>
        <button
          type="button"
          class="p-sidebar__logout"
          :title="$t ? ($t('btn.logout') || 'Logout') : 'Logout'"
          @click="onLogout"
        >
          <i class="material-icons-round">logout</i>
        </button>
      </div>
    </div>
  </aside>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'AdminSidebar',
  data() {
    return {
      openCat: '',
      hoverCat: '',
      hoverTimer: null,
    };
  },
  computed: {
    ...mapGetters('layout', ['isSidebarCollapsed']),
    collapsed() {
      return this.isSidebarCollapsed;
    },
    userName() {
      const u = this.$store.state.auth?.user;
      return u?.name || u?.username || u?.email?.split('@')[0] || 'Admin';
    },
    userInitial() {
      const n = this.userName || 'A';
      return n.trim().charAt(0).toUpperCase();
    },
    currentRouteName() {
      return this.$route?.name || '';
    },
    categories() {
      // $t() 호출은 반응형 — locale 변경 시 computed 재평가 → 메뉴 라벨 즉시 갱신
      const t = this.$t;
      return [
        {
          id: 'dashboards',
          label: t('navi.dash'),
          icon: 'dashboard',
          direct: 'Sales',
          match: ['Default', 'Sales'],
          items: [],
        },
        {
          id: 'contents',
          label: t('navi.04'),
          icon: 'menu_book',
          items: [
            { name: 'ContentsTextbooks',  label: t('navi.04_01'), match: ['ContentsTextbooks', 'EditTextbook'] },
          ],
        },
        {
          id: 'settlement',
          label: t('navi.02'),
          icon: 'request_quote',
          items: [
            { name: 'SettlementSummary',   label: t('navi.02_04') },
            { name: 'SettlementCompleted', label: t('navi.02_02') },
          ],
        },
        {
          id: 'helpDesk',
          label: t('navi.07'),
          icon: 'contact_support',
          items: [
            { name: 'HelpDeskInquiry', label: t('navi.07_03') },
            { name: 'HelpDeskFAQs',    label: t('navi.07_02') },
          ],
        },
      ];
    },
  },
  watch: {
    currentRouteName: {
      immediate: true,
      handler(name) {
        if (!name) return;
        const cat = this.categories.find(c => !c.direct && c.items.some(it => this.matches(it, name)));
        if (cat) this.openCat = cat.id;
      },
    },
  },
  methods: {
    ...mapActions('layout', ['toggleSidebar']),
    matches(item, name) {
      if (item.name === name) return true;
      if (Array.isArray(item.match)) return item.match.includes(name);
      return false;
    },
    isItemActive(item) {
      return this.matches(item, this.currentRouteName);
    },
    isCategoryActive(cat) {
      if (Array.isArray(cat.match) && cat.match.includes(this.currentRouteName)) return true;
      return cat.items.some(it => this.isItemActive(it));
    },
    onCatClick(cat) {
      if (this.collapsed) return;
      this.openCat = this.openCat === cat.id ? '' : cat.id;
    },
    onCatEnter(cat) {
      if (!this.collapsed) return;
      clearTimeout(this.hoverTimer);
      this.hoverCat = cat.id;
    },
    onCatLeave() {
      if (!this.collapsed) return;
      this.hoverTimer = setTimeout(() => { this.hoverCat = ''; }, 120);
    },
    onLogout() {
      this.$router.push({ name: 'Logout' }).catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/pineapple';

$sidebar-w: 256px;
$sidebar-w-collapsed: 64px;

.p-sidebar {
  position: fixed;
  inset: 0 auto 0 0;
  width: $sidebar-w;
  background: $p-sidebar;
  background-image: radial-gradient(circle at 20% 10%, rgba(255, 201, 60, 0.04) 0%, transparent 40%);
  color: $p-sidebar-tx;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  transition: width 0.2s ease;
  font-family: $p-font-body;

  &.is-collapsed {
    width: $sidebar-w-collapsed;
  }
}

.p-sidebar__brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px 16px 14px;
  border-bottom: 1px solid $p-sidebar-line;
  text-decoration: none;
  color: inherit;
  text-align: center;
  flex-shrink: 0;

  &:hover { color: inherit; }

  .is-collapsed & {
    padding: 14px 6px;
    gap: 0;
  }
}

.p-sidebar__logo {
  width: 56px;
  height: 56px;
  display: grid;
  place-items: center;
  flex-shrink: 0;

  > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .is-collapsed & {
    width: 36px;
    height: 36px;
  }
}

.p-sidebar__brand-text {
  min-width: 0;
  text-align: center;

  .is-collapsed & { display: none; }
}

.p-sidebar__brand-name {
  font-size: 20px;
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: $p-sidebar-tx;
}

.p-sidebar__brand-sub {
  font-size: 11px;
  color: $p-sidebar-mid;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  margin-top: 6px;
}

.p-sidebar__nav {
  flex: 1;
  padding: 12px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  // No overflow-y: auto — would clip flyouts in collapsed mode.
  // With current category count, viewport accommodates all entries.
  overflow: visible;
}

// Categories
.p-cat {
  position: relative;
  display: block;

  // Hover background only applies to non-direct expandable rows on hover
  &.is-active {
    .p-cat__accent { opacity: 1; }
    .p-cat__icon   { color: $p-point; }
    .p-cat__label  { color: $p-sidebar-tx; font-weight: 800; }
  }
}

.p-cat__head,
.p-cat--direct {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 14px 14px 18px;
  background: transparent;
  border: 0;
  color: $p-sidebar-tx;
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  text-align: left;
  text-decoration: none;
  transition: background 0.12s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }

  .is-collapsed & {
    justify-content: center;
    padding: 14px 0;
  }
}

.p-cat--direct {
  &.is-active {
    .p-cat__accent { opacity: 1; }
    .p-cat__icon   { color: $p-point; }
    .p-cat__label  { color: $p-sidebar-tx; font-weight: 800; }
  }
}

// Yellow left accent bar (active state)
.p-cat__accent {
  position: absolute;
  left: 6px;
  top: 8px;
  bottom: 8px;
  width: 4px;
  background: $p-point;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.15s ease;

  .is-collapsed & {
    left: 2px;
    width: 3px;
  }
}

.p-cat__icon {
  font-size: 22px !important;
  color: $p-sidebar-mid;
  flex-shrink: 0;
  transition: color 0.15s ease;
}

.p-cat__label {
  flex: 1;
  color: $p-sidebar-tx;
  transition: color 0.15s ease, font-weight 0.15s ease;
}

.p-cat__count {
  font-family: $p-font-mono;
  font-size: 11px;
  font-weight: 700;
  color: $p-sidebar-mid;
  background: rgba(255, 255, 255, 0.06);
  padding: 2px 8px;
  border-radius: 5px;
}

.p-cat__chev {
  font-size: 18px !important;
  color: $p-sidebar-mid;
}

.p-cat__items {
  padding: 2px 0 6px 38px;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.p-cat__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: $p-sidebar-mid;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.12s ease, color 0.12s ease;

  .p-cat__item-dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: $p-sidebar-mid;
    flex-shrink: 0;
    transition: background 0.12s ease;
  }

  &:hover { color: $p-sidebar-tx; }

  &.is-active {
    color: $p-point;
    font-weight: 700;
    background: $p-sidebar-item-active;

    .p-cat__item-dot { background: $p-point; }
  }
}

// Hover flyout (collapsed mode) - now visible since overflow: visible on nav
.p-cat__flyout {
  position: absolute;
  top: 0;
  left: calc(100% + 6px);
  min-width: 220px;
  background: $p-sidebar;
  border: 1px solid $p-sidebar-line;
  border-radius: 10px;
  padding: 8px 0;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.45);
  z-index: 1001;

  // 10px transparent bridge to keep hover continuous (Checklist §1)
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -10px;
    width: 10px;
    background: transparent;
  }
}

.p-cat__flyout-title {
  padding: 6px 16px 10px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  color: $p-point;
  text-transform: uppercase;
  border-bottom: 1px solid $p-sidebar-line;
  margin-bottom: 6px;
}

.p-cat__flyout-item {
  display: block;
  padding: 9px 16px;
  font-size: 13px;
  color: $p-sidebar-tx;
  text-decoration: none;
  white-space: nowrap;

  &:hover { background: $p-sidebar-alt; }

  &.is-active {
    color: $p-point;
    font-weight: 700;
    background: $p-sidebar-item-active;
  }
}

.p-sidebar__footer {
  border-top: 1px solid $p-sidebar-line;
  padding: 14px;
}

.p-sidebar__user {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.p-sidebar__avatar {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: $p-point-soft;
  color: $p-point-deep;
  border: 1px solid $p-point-deep;
  display: grid;
  place-items: center;
  font-family: $p-font-mono;
  font-weight: 800;
  font-size: 14px;
  flex-shrink: 0;
}

.p-sidebar__user-info {
  flex: 1;
  min-width: 0;
}

.p-sidebar__user-name {
  font-size: 13px;
  font-weight: 800;
  color: $p-sidebar-tx;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.p-sidebar__user-role {
  font-size: 10px;
  color: $p-sidebar-mid;
  margin-top: 2px;
}

.p-sidebar__logout {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border: 0;
  background: transparent;
  color: $p-sidebar-mid;
  cursor: pointer;
  border-radius: 6px;
  flex-shrink: 0;

  &:hover {
    background: $p-sidebar-alt;
    color: $p-err;
  }

  i { font-size: 16px; }
}
</style>
